<template>
  <div>
    <GoogleMap
      ref="mapRef"
      class="h-full w-full"
      :language="$l10n.language"
      :api-key
      :center
      :zoom="area.zoom"
      gesture-handling="greedy"
      :scrollwheel="true"
      :clickable-icons="false"
      :street-view-control="true"
      :map-type-control="false"
      :disable-default-ui="true"
      @idle="handleIdle"
    >
      <template #default="{ map: googleMap, api }">
        <RedesignMapCardsOverlay
          v-if="selectedItem && filters"
          :item="selectedItem"
          :filters="filters"
          :map="googleMap"
          :api
          @close="closeAccommodationWindow"
        />

        <CustomMarker v-if="isSingleMode" :options="{ position: center }">
          <div
            class="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-bgr shadow-[0_0_1rem_0.5rem_rgba(0,0,0,0.4)]"
          >
            <WebccIcon name="site/house" class="h-6 w-6" />
          </div>
        </CustomMarker>

        <CustomMarker
          v-else
          v-for="item in items"
          :key="JSON.stringify(item.coords)"
          :options="{ position: toLatLng(item.coords) }"
          @click.stop="handleMarkerClick(item)"
        >
          <RedesignMapMarkerCluster v-if="item.type === 'cluster'" :item />
          <RedesignMapMarkerAccom
            v-if="item.type === 'accommodation'"
            :item
            :selected="item.key === selectedItem?.key"
            :visited="visitedItems.has(item.key)"
          />
        </CustomMarker>

        <CustomControl position="TOP_RIGHT" :index="1">
          <CustomFullscreenControl :map="googleMap" />
        </CustomControl>

        <CustomControl position="RIGHT_TOP" :index="2">
          <CustomZoomControl :map="googleMap" />
        </CustomControl>

        <CustomControl position="TOP_LEFT" :index="3">
          <CustomMapTypeControl :map="googleMap" />
        </CustomControl>
      </template>
    </GoogleMap>
  </div>
</template>

<script setup lang="ts">
import { GoogleMap, CustomControl, CustomMarker } from 'vue3-google-map'
import { toLatLng } from '~/utils/map'

const props = defineProps<{
  filters?: SearchApiParams
  viewport?: Viewport
  accom?: TranslatedAccommodation
}>()

const { apiKey } = toRefs(useRuntimeConfig().public.googlemaps)
const settings = useSettings()
const visitedItems = useVisitedItems<string>()

const mapRef = ref<InstanceType<typeof GoogleMap>>()
const map = computed(() => mapRef.value?.map)
const mapDim = computed(() => getMapDim(mapRef.value?.$el, settings))
const isMapReady = computed(() => !!mapRef.value?.ready)

const { viewport, filters, accom } = toRefs(props)

const isSingleMode = computed(() => !!accom.value?.coordinates)

const area = ref({
  bounds: getBoundsDefault(),
  zoom: getZoomForBounds(getBoundsDefault()),
})
const isAreaUpdating = ref(false)
const center = computed(() => {
  if (isSingleMode.value) {
    return {
      lat: accom.value!.coordinates.lat!,
      lng: accom.value!.coordinates.lng!,
    }
  }
  return { lat: 45, lng: 10 }
})
const selectedItem = ref<MapItemAccom>()

const { data: items } = useRedesignMapSearchBoundsQuery(area, filters, {
  enabled: computed(() => isMapReady.value && !isSingleMode.value),
})

watch(isMapReady, (isReady) => isReady && searchForViewport())
watchDeep(viewport, searchForViewport)

function searchForViewport() {
  const bounds = isSingleMode.value
    ? getBoundsFromCoords(center.value)
    : viewport.value
      ? getBoundsFromViewport(viewport.value)
      : getBoundsDefault()
  const zoom = getZoomForBounds(bounds, mapDim.value)
  updateArea(bounds, zoom)
  map.value?.fitBounds(toLatLngBounds(bounds), settings.mapBoundsPadding)
}

function updateArea(bounds: Bounds, zoom: number, centerOn?: Coords) {
  if (isBoundsEqual(area.value.bounds, bounds) && !zoom && !centerOn) return

  area.value = { bounds, zoom }

  isAreaUpdating.value = true

  if (zoom) map.value?.setZoom(zoom)
  if (centerOn) map.value?.panTo(toLatLng(centerOn))

  setTimeout(() => (isAreaUpdating.value = false), 100)
}

function handleIdle() {
  if (isAreaUpdating.value) return
  if (isBoundsEqual(area.value.bounds, getMapBounds(map.value!), 0.001)) return

  updateArea(getMapBounds(map.value!), map.value?.getZoom() ?? 0)
}

function handleMarkerClick(item: MapItem) {
  if (item.type === 'cluster') return zoomAndPanMapTo(item)
  if (item.type === 'accommodation') return showAccommodationWindow(item)
}

function zoomAndPanMapTo(item: MapItemCluster) {
  const bounds = getBoundsFromCoordBounds(item.bounds)
  const zoom = Math.max(
    (map.value?.getZoom() ?? 0) + 1,
    getZoomForBounds(bounds, mapDim.value),
  )
  updateArea(bounds, zoom, item.coords)
}

function showAccommodationWindow(item: MapItemAccom) {
  selectedItem.value = item
  visitedItems.add(item.key)
}

function closeAccommodationWindow() {
  selectedItem.value = undefined
}
</script>
